import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Package, ExternalLink, FileText } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { receptionsAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import ManualReceptionDetailDialog from '@/components/ManualReceptionDetailDialog';

/**
 * Side drawer that lists individual reception events for a given product.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   industrialProductId: string | null
 *   supplierProductId: string | null  (if set, overrides industrialProductId)
 *   productName: string  (display title)
 *   subtitle: string     (optional subtitle, e.g. supplier name)
 */
export default function ProductReceptionsDrawer({
    open,
    onClose,
    industrialProductId = null,
    supplierProductId = null,
    productName = '',
    subtitle = '',
    initialStartDate = null,
    initialEndDate = null,
    siteIds = null, // array of site ids | null for all
    supplierId = null,
}) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const todayIso = new Date().toISOString().split('T')[0];
    const ninetyAgoIso = new Date(new Date().setDate(new Date().getDate() - 90)).toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(initialStartDate || ninetyAgoIso);
    const [endDate, setEndDate] = useState(initialEndDate || todayIso);
    const [previewMrId, setPreviewMrId] = useState(null);
    const navigate = useNavigate();

    // Sync internal date range with props each time the drawer is (re)opened
    useEffect(() => {
        if (open) {
            if (initialStartDate) setStartDate(initialStartDate);
            if (initialEndDate) setEndDate(initialEndDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialStartDate, initialEndDate]);

    const openReception = (r) => {
        if (!r.source_id) return;
        if (r.source_type === 'manual_reception') {
            // Open standalone read-only dialog directly inside the drawer
            setPreviewMrId(r.source_id);
            return;
        }
        // Orders: keep navigation flow (less frequent case from receptions)
        try {
            sessionStorage.setItem('pendingDrawerReopen', JSON.stringify({
                industrial_product_id: industrialProductId,
                supplier_product_id: supplierProductId,
                productName,
                subtitle,
                startDate,
                endDate,
                siteIds,
                returnPath: window.location.pathname + window.location.search,
            }));
        } catch { /* ignore */ }
        navigate(`/orders?tab=orders&openOrder=${r.source_id}`);
        onClose();
    };

    const siteIdsKey = Array.isArray(siteIds) ? siteIds.join(',') : '';
    useEffect(() => {
        if (!open || (!industrialProductId && !supplierProductId)) return;
        setLoading(true);
        const params = { start_date: startDate, end_date: endDate };
        if (supplierProductId) params.supplier_product_id = supplierProductId;
        else if (industrialProductId) params.industrial_product_id = industrialProductId;
        if (siteIdsKey) {
            params.site_ids = siteIdsKey;
        }
        if (supplierId) {
            params.supplier_id = supplierId;
        }
        receptionsAPI.byProduct(params)
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [open, industrialProductId, supplierProductId, startDate, endDate, siteIdsKey, supplierId]);

    const setRange = (days) => {
        setStartDate(new Date(new Date().setDate(new Date().getDate() - days)).toISOString().split('T')[0]);
        setEndDate(todayIso);
    };

    return (
        <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="product-receptions-drawer">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-orange-500" />
                        {productName || 'Réceptions du produit'}
                    </SheetTitle>
                    {subtitle && (
                        <SheetDescription>{subtitle}</SheetDescription>
                    )}
                </SheetHeader>

                {/* Filters */}
                <div className="mt-4 flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Du</Label>
                        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8 text-xs w-36" data-testid="drawer-start-date" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Au</Label>
                        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-8 text-xs w-36" data-testid="drawer-end-date" />
                    </div>
                    <div className="flex gap-1 ml-auto">
                        <button className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200" onClick={() => setRange(30)}>30j</button>
                        <button className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200" onClick={() => setRange(90)}>90j</button>
                        <button className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200" onClick={() => setRange(180)}>6 mois</button>
                        <button className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200" onClick={() => { setStartDate('2020-01-01'); setEndDate(todayIso); }}>Tout</button>
                    </div>
                </div>

                {/* Summary */}
                {data && (
                    <div className="mt-4 grid grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200 text-center">
                        <div>
                            <div className="text-[10px] uppercase text-slate-400 tracking-wider">Réceptions</div>
                            <div className="text-lg font-bold text-slate-800">{data.count}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase text-slate-400 tracking-wider">Quantité totale</div>
                            <div className="text-lg font-bold text-emerald-600">{data.total_quantity?.toFixed(2)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase text-slate-400 tracking-wider">Valeur totale</div>
                            <div className="text-lg font-bold text-orange-600">{formatCurrency(data.total_value || 0)}</div>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="mt-4">
                    {loading ? (
                        <div className="py-8 flex items-center justify-center text-slate-400 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
                        </div>
                    ) : !data || data.count === 0 ? (
                        <div className="py-12 text-center text-slate-400">
                            <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Aucune réception sur la période sélectionnée</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr>
                                        <th className="text-left px-2 py-1.5 font-medium">Date</th>
                                        <th className="text-left px-2 py-1.5 font-medium">N° BL</th>
                                        <th className="text-left px-2 py-1.5 font-medium">Site</th>
                                        <th className="text-left px-2 py-1.5 font-medium">Fournisseur / Produit</th>
                                        <th className="text-right px-2 py-1.5 font-medium">Qté</th>
                                        <th className="text-right px-2 py-1.5 font-medium">PU</th>
                                        <th className="text-right px-2 py-1.5 font-medium">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.receptions.map((r) => (
                                        <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`reception-row-${r.id}`}>
                                            <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{r.date}</td>
                                            <td className="px-2 py-1.5 font-mono text-slate-700">
                                                <button
                                                    type="button"
                                                    onClick={() => openReception(r)}
                                                    className="inline-flex items-center gap-1 hover:text-orange-600 hover:underline"
                                                    title="Ouvrir la réception"
                                                    data-testid={`open-reception-${r.id}`}
                                                >
                                                    {r.source_type === 'manual_reception' ? (
                                                        <FileText className="w-3 h-3 text-blue-400" />
                                                    ) : (
                                                        <ExternalLink className="w-3 h-3 text-orange-400" />
                                                    )}
                                                    {r.document_number || '—'}
                                                </button>
                                            </td>
                                            <td className="px-2 py-1.5 text-slate-600">{r.site_name}</td>
                                            <td className="px-2 py-1.5">
                                                <div className="text-slate-800">{r.supplier_name}</div>
                                                <div className="text-[10px] text-slate-400 italic">{r.supplier_name_product}</div>
                                            </td>
                                            <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                                <span className="font-semibold text-emerald-600">{r.quantity.toFixed(2)}</span>
                                                {r.invoice_unit && <span className="ml-1 text-[10px] text-slate-400">{r.invoice_unit}</span>}
                                            </td>
                                            <td className="px-2 py-1.5 text-right text-slate-600 whitespace-nowrap">{formatCurrency(r.unit_price || 0)}</td>
                                            <td className="px-2 py-1.5 text-right font-semibold text-slate-800 whitespace-nowrap">{formatCurrency(r.total || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </SheetContent>

            <ManualReceptionDetailDialog
                receptionId={previewMrId}
                onClose={() => setPreviewMrId(null)}
            />
        </Sheet>
    );
}
